import { useEffect } from 'react';
import { AllowedExperimentKey, TenantId } from '@tenants/types';
import { ExperimentObservabilityService } from 'observability/services/experiment-observability';
import { AnalyticsController } from 'analytics/analytics-controller';

/**
 * Track which experiments have been tracked for analytics (prevents duplicate tracking)
 */
const trackedExperiments = new Set<string>();

/**
 * Hook to track experiment views for analytics.
 * 
 * Tracks analytics once per experiment key per page load.
 * 
 * Note: Circuit breaker decision tracking happens during SSR evaluation,
 * so we only need to track the analytics "viewed" event here.
 * 
 * @param experimentKey - The experiment key being tracked
 * @param variationId - The variation ID (0 = control, 1 = variation)
 * @param tenantId - Optional tenant ID for proper tracking
 */
export function useExperimentTracking(
  experimentKey: AllowedExperimentKey,
  variationId: number,
  tenantId: TenantId
): void {
  // Track experiment view once per experiment key (client-side only)
  useEffect(() => {
    if (trackedExperiments.has(experimentKey)) return;
    trackedExperiments.add(experimentKey);
    
    // Track for observability (NewRelic/Console)
    ExperimentObservabilityService.trackExperimentViewed(experimentKey, variationId, tenantId);
    
    // Track for analytics (Google Tag Manager)
    // This matches the behavior of GrowthBook's trackingCallback
    AnalyticsController.trackGrowthbookEvent(experimentKey, variationId);
  }, [experimentKey, variationId, tenantId]);
}
